// src/pages/Documenten.js
// vDOC-008

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

// ---------------- helpers ----------------
function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[.,;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function firstWord(s) {
  const t = norm(s);
  return t.split(" ").filter(Boolean)[0] || "";
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

function pickOrgName(o) {
  return (
    o?.search_name ||
    o?.zoeknaam ||
    o?.zoek_naam ||
    o?.name ||
    o?.naam ||
    o?.bedrijfsnaam ||
    o?.organisation_name ||
    o?.organisatie_naam ||
    o?.organisatie ||
    ""
  );
}

function pickOrgSearchName(o) {
  return (
    o?.search_name ||
    o?.zoeknaam ||
    o?.zoek_naam ||
    o?.organisation_search_name ||
    o?.organisatie_zoeknaam ||
    pickOrgName(o)
  );
}

function isPrivateRelation(o) {
  const entityType = String(
    o?.relation_entity_type ||
    o?.relation_type ||
    o?.entity_type ||
    ""
  )
    .toLowerCase()
    .trim();

  return (
    entityType === "private" ||
    entityType === "particulier" ||
    entityType === "person" ||
    entityType === "persoon"
  );
}

function pickContactName(c) {
  const fn = String(c?.first_name || c?.voornaam || "").trim();
  const ln = String(c?.last_name || c?.achternaam || "").trim();
  const full = String(c?.full_name || c?.naam || "").trim();

  return (
    c?.search_name ||
    c?.zoeknaam ||
    c?.zoek_naam ||
    c?.display_name ||
    c?.weergavenaam ||
    (ln && fn ? `${ln}, ${fn}` : "") ||
    full ||
    fn ||
    ""
  );
}

function pickItemTitle(it) {
  return it?.file_name || "";
}
function pickItemBody(it) {
  return it?.description || "";
}
function pickItemOrgName(it) {
  return it?.organisation_name || it?.organisatie_naam || it?.organisatie || "";
}
function pickItemContactName(it) {
  return it?.contact_name || it?.contactpersoon || it?.contactpersoon_naam || "";
}

function pickItemPersonnelName(it) {
  return it?.personnel_name || it?.personeel_naam || it?.personeel || "";
}

function pickItemPhone(it) {
  return it?.personnel_phone || it?.organisation_phone || it?.phone || it?.telefoon || "";
}

function pickItemMobile(it) {
  return it?.personnel_mobile || it?.organisation_mobile || it?.mobile || it?.mobiel || "";
}

function pickItemEmail(it) {
  return it?.personnel_email || it?.organisation_email || it?.email || "";
}
function pickItemStatus(it) {
  return it?.status || "";
}

function formatDocumentType(v) {
  const raw = String(v || "").trim();
  const t = raw.toLowerCase();

  if (t === "general") return "Algemeen";
  if (t === "offer") return "Offerte";
  if (t === "invoice") return "Factuur";
  if (t === "project") return "Project";
  if (t === "contract") return "Contract";
  if (t === "other") return "Overig";
  if (t === "pers_krt" || t === "personnel_matching" || t === "personnel_match") return "Pers_krt";

  return raw || "";
}

function pickItemDueISO(it) {
  const v = it?.document_date || "";
  return String(v || "").slice(0, 10);
}

function formatDateNL(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

async function tryFetchJson(urls, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  let lastErr;
  for (const url of urls) {
    try {
      const res = await fetch(buildUrl(url), { ...options, headers });
      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        throw new Error(`${res.status} ${res.statusText}${msg ? ` - ${msg}` : ''}`);
      }
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) return await res.json();
      return await res.text();
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('Failed to fetch');
}

function normalizePhoneForCommunication(raw, defaultCountryCode = "+31") {
  let s = String(raw || "").trim();

  if (!s || s === "-") return "";

  s = s
    .replace(/[^\d+]/g, "")
    .replace(/^00/, "+");

  if (!s) return "";

  if (s.startsWith("+")) return s;

  if (s.startsWith("0")) {
    return `${defaultCountryCode}${s.slice(1)}`;
  }

  if (s.startsWith("31")) {
    return `+${s}`;
  }

  return `${defaultCountryCode}${s}`;
}

async function loadCommunicationSettingsSafe() {
  try {
    const data = await tryFetchJson(["/api/communication-settings"]);

    return {
      email_enabled: data?.email_enabled !== false,
      whatsapp_enabled: data?.whatsapp_enabled !== false,
      sms_enabled: data?.sms_enabled === true,
      default_country_code: data?.default_country_code || "+31",
      default_sms_text: data?.default_sms_text || "",
      default_whatsapp_text: data?.default_whatsapp_text || "",
    };
  } catch {
    return {
      email_enabled: true,
      whatsapp_enabled: true,
      sms_enabled: false,
      default_country_code: "+31",
      default_sms_text: "",
      default_whatsapp_text: "",
    };
  }
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function askCommunicationChoice(raw) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");

    overlay.style.position = "fixed";
    overlay.style.left = "0";
    overlay.style.top = "0";
    overlay.style.right = "0";
    overlay.style.bottom = "0";
    overlay.style.background = "rgba(0,0,0,0.35)";
    overlay.style.zIndex = "999999";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.padding = "18px";
    overlay.style.boxSizing = "border-box";

    const box = document.createElement("div");

    box.style.width = "min(520px, 100%)";
    box.style.background = "#fff";
    box.style.borderRadius = "12px";
    box.style.boxShadow = "0 18px 50px rgba(0,0,0,0.35)";
    box.style.padding = "18px";
    box.style.boxSizing = "border-box";
    box.style.fontFamily = "Arial, Helvetica, sans-serif";
    box.style.color = "#111";

    const title = document.createElement("div");
    title.style.fontWeight = "700";
    title.style.fontSize = "18px";
    title.style.marginBottom = "12px";
    title.textContent = `Wat wil je doen met ${raw}?`;

    const help = document.createElement("div");
    help.style.fontSize = "16px";
    help.style.lineHeight = "1.8";
    help.style.marginBottom = "12px";
    help.innerHTML = "1 = Bellen<br/>2 = SMS<br/>3 = WhatsApp";

    const input = document.createElement("input");
    input.type = "text";
    input.value = "3";
    input.inputMode = "numeric";
    input.autocomplete = "off";
    input.style.width = "100%";
    input.style.fontSize = "20px";
    input.style.padding = "10px 12px";
    input.style.border = "2px solid #1f4e79";
    input.style.borderRadius = "8px";
    input.style.boxSizing = "border-box";
    input.style.marginBottom = "14px";

    const buttons = document.createElement("div");
    buttons.style.display = "flex";
    buttons.style.gap = "10px";
    buttons.style.justifyContent = "flex-end";

    const okButton = document.createElement("button");
    okButton.type = "button";
    okButton.textContent = "OK";
    okButton.style.padding = "10px 18px";
    okButton.style.border = "none";
    okButton.style.borderRadius = "8px";
    okButton.style.background = "#1f4e79";
    okButton.style.color = "#fff";
    okButton.style.fontSize = "16px";
    okButton.style.cursor = "pointer";

    const cancelButton = document.createElement("button");
    cancelButton.type = "button";
    cancelButton.textContent = "Annuleren";
    cancelButton.style.padding = "10px 18px";
    cancelButton.style.border = "1px solid #d0d0d0";
    cancelButton.style.borderRadius = "8px";
    cancelButton.style.background = "#fff";
    cancelButton.style.color = "#111";
    cancelButton.style.fontSize = "16px";
    cancelButton.style.cursor = "pointer";

    let done = false;

    function cleanup(value) {
      if (done) return;
      done = true;

      try {
        document.body.removeChild(overlay);
      } catch {}

      resolve(value);
    }

    okButton.onclick = () => cleanup(input.value);
    cancelButton.onclick = () => cleanup(null);

    input.onkeydown = (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        cleanup(input.value);
      }

      if (ev.key === "Escape") {
        ev.preventDefault();
        cleanup(null);
      }
    };

    overlay.onclick = (ev) => {
      if (ev.target === overlay) {
        cleanup(null);
      }
    };

    buttons.appendChild(cancelButton);
    buttons.appendChild(okButton);

    box.appendChild(title);
    box.appendChild(help);
    box.appendChild(input);
    box.appendChild(buttons);

    overlay.appendChild(box);
    document.body.appendChild(overlay);

    setTimeout(() => {
      try {
        input.focus();
        input.select();
      } catch {}
    }, 80);
  });
}

async function handleEmailGridClick(e, email) {
  stopGridClick(e);

  const mail = String(email || "").trim();

  if (!mail || mail === "-") return;

  const settings = await loadCommunicationSettingsSafe();

  if (settings.email_enabled === false) {
    alert("E-mail is niet actief in Communicatie-instellingen.");
    return;
  }

  window.location.href = `mailto:${encodeURIComponent(mail)}`;
}

function handlePhoneGridClick(e, phone) {
  stopGridClick(e);

  const tel = String(phone || "").trim();

  if (!tel || tel === "-") return;

  window.location.href = `tel:${tel}`;
}

async function handleMobileGridClick(e, mobile) {
  stopGridClick(e);

  const raw = String(mobile || "").trim();

  if (!raw || raw === "-") return;

  const settings = await loadCommunicationSettingsSafe();
  const normalized = normalizePhoneForCommunication(raw, settings.default_country_code || "+31");

  if (!normalized) return;

  const choice = await askCommunicationChoice(raw);

  const c = String(choice || "").trim().toLowerCase();

  if (!c) return;

  if (c === "1" || c === "bellen" || c === "bel") {
    window.location.href = `tel:${raw}`;
    return;
  }

  if (c === "2" || c === "sms") {
    if (settings.sms_enabled !== true) {
      alert("SMS is niet actief in Communicatie-instellingen.");
      return;
    }

    const text = window.prompt(
      `SMS naar ${raw}`,
      settings.default_sms_text || ""
    );

    if (text === null) return;

    const message = String(text || "").trim();

    if (!message) {
      alert("SMS tekst ontbreekt.");
      return;
    }

    try {
      await tryFetchJson(["/api/communications/sms"], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: raw,
          text: message,
        }),
      });

      alert(`SMS verstuurd naar ${raw}`);
    } catch (err) {
      alert(err?.message || "SMS verzenden mislukt.");
    }

    return;
  }

  if (c === "3" || c === "whatsapp" || c === "app") {
    if (settings.whatsapp_enabled === false) {
      alert("WhatsApp is niet actief in Communicatie-instellingen.");
      return;
    }

    const whatsappNumber = normalized.replace(/[^\d]/g, "");
    const text = String(settings.default_whatsapp_text || "").trim();
    const suffix = text ? `?text=${encodeURIComponent(text)}` : "";

    window.open(`https://wa.me/${whatsappNumber}${suffix}`, "_blank", "noopener,noreferrer");
  }
}

function capFirst(s) {
  const v = String(s || "").trim();
  if (!v) return "";
  return v.charAt(0).toUpperCase() + v.slice(1);
}

function replacePuntToken(s) {
  const v = String(s || "");
  return v
    .replace(/\bpunt\b/gi, ".")
    .replace(/\bpuntje\b/gi, ".")
    .replace(/\s+\./g, ".");
}

// ---- date speech parsing (NL months + digits) ----
const MONTHS_NL = {
  januari: 1, jan: 1,
  februari: 2, feb: 2,
  maart: 3, mrt: 3,
  april: 4, apr: 4,
  mei: 5,
  juni: 6, jun: 6,
  juli: 7, jul: 7,
  augustus: 8, aug: 8,
  september: 9, sep: 9,
  okt: 10, oktober: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
};

function parseDateFromSpeechToISO(raw) {
  const t0 = String(raw || "").trim();
  if (!t0) return "";

  const digits = t0.replace(/\D/g, "");
  if (digits.length === 8) {
    const dd = digits.slice(0, 2);
    const mm = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    const iso = `${yyyy}-${mm}-${dd}`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  }

  const m1 = t0.match(/(\d{1,2})\D+(\d{1,2})\D+(\d{4})/);
  if (m1) {
    const dd = String(m1[1]).padStart(2, "0");
    const mm = String(m1[2]).padStart(2, "0");
    const yyyy = String(m1[3]);
    return `${yyyy}-${mm}-${dd}`;
  }

  const t = norm(t0);
  const m2 = t.match(/^(\d{1,2})\s+([a-z]+)\s+(\d{4})$/i);
  if (m2) {
    const dd = String(m2[1]).padStart(2, "0");
    const monName = String(m2[2] || "").toLowerCase();
    const yyyy = String(m2[3]);
    const mon = MONTHS_NL[monName];
    if (mon) {
      const mm = String(mon).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
  }

  return "";
}

function upsertById(arr, item) {
  const id = item?.id;
  if (id === null || id === undefined) return arr;
  const idx = arr.findIndex((x) => String(x.id) === String(id));
  if (idx === -1) return [item, ...arr];
  const copy = arr.slice();
  copy[idx] = { ...copy[idx], ...item };
  return copy;
}

function removeById(arr, id) {
  return arr.filter((x) => String(x.id) !== String(id));
}

// ---------------- component ----------------
const emptyForm = {
  id: null,
  document_type: "",
  organisation_id: "",
  contact_id: "",
  document_date: "",
  file_name: "",
  description: "",
};

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function Documenten() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

  let role = "user";

  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

  const isReadonly = String(role || "").toLowerCase() === "readonly";
  const VERSION = "vDOC-008";

  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [listIndex, setListIndex] = useState(0);

const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [saveError, setSaveError] = useState(null);
  const [flash, setFlash] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [speechInfo, setSpeechInfo] = useState("");

const [q, setQ] = useState(getParam("q"));
const [type, setType] = useState(getParam("type"));
const [titleFilter, setTitleFilter] = useState("");
const [dueFilter, setDueFilter] = useState("");
const [orgFilterId, setOrgFilterId] = useState(getParam("organisation_id") || getParam("organisatie_id"));
const [contactFilterId, setContactFilterId] = useState(getParam("contact_id") || getParam("contactpersoon_id"));
const [personnelFilter, setPersonnelFilter] = useState("");
const [phoneFilter, setPhoneFilter] = useState("");
const [mobileFilter, setMobileFilter] = useState("");
const [emailFilter, setEmailFilter] = useState("");
const [mode, setMode] = useState("search"); // search | list | form
const isFormLocked = mode !== "form";
const [sort, setSort] = useState({ key: "default", dir: "asc" });

const EMPTY_VAL = "__EMPTY__";

const searchRef = useRef(null);
const rowRefs = useRef([]);
  const midScrollRef = useRef(null);
  const rightScrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const uploadButtonRef = useRef(null);

const formRefs = {
  type: useRef(null),
  org: useRef(null),
  contact: useRef(null),
  due: useRef(null),
  title: useRef(null),
  body: useRef(null),
};

  const selectedRelation = orgs.find(
    (o) => String(o.id) === String(form.organisation_id || "")
  );

  const selectedRelationIsPrivate = isPrivateRelation(selectedRelation);

  const formOrder = selectedRelationIsPrivate
    ? ["type", "org", "due", "title", "body"]
    : ["type", "org", "contact", "due", "title", "body"];

  useEffect(() => {
    if (!selectedRelationIsPrivate) return;
    if (!form.contact_id) return;

    setForm((prev) => ({
      ...prev,
      contact_id: "",
    }));
  }, [selectedRelationIsPrivate, form.contact_id]);

  const [focusIndex, setFocusIndex] = useState(0);

  // ✅ NEW: "grote tekst editor" modus + door-dicteren modus
  const [bodyEditor, setBodyEditor] = useState(false);
  const bodyDictateActiveRef = useRef(false);
  const bodyDictateRunsRef = useRef(0);

  function focusByIndex(idx) {
    const key = formOrder[Math.max(0, Math.min(formOrder.length - 1, idx))];
    const r = formRefs[key]?.current;
    if (r && typeof r.focus === "function") r.focus();
  }
  function focusNext() {
    setMode("form");
    setFocusIndex((i) => {
      const n = Math.min(formOrder.length - 1, i + 1);
      setTimeout(() => focusByIndex(n), 0);
      return n;
    });
  }
  function focusPrev() {
    setMode("form");
    setFocusIndex((i) => {
      const n = Math.max(0, i - 1);
      setTimeout(() => focusByIndex(n), 0);
      return n;
    });
  }

  function scrollToIndex(idx) {
    const el = rowRefs.current[idx];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }

  function highlightIndex(idx, arrLen) {
    const n = arrLen || 0;
    if (!n) {
      setListIndex(0);
      return;
    }
    const i = Math.max(0, Math.min(n - 1, idx));
    setListIndex(i);
    setMode("list");
    setTimeout(() => scrollToIndex(i), 0);
  }

  function formHasId() {
    return form.id !== null && form.id !== undefined && String(form.id).length > 0;
  }

function loadItemFromRow(it) {
  setSelectedId(it.id);
  setForm({
    id: it.id,
    document_type: it.document_type || "",
    organisation_id:
      it.organisation_id !== null && it.organisation_id !== undefined
        ? String(it.organisation_id)
        : String(it.organisatie_id || ""),
    contact_id:
      it.contact_id !== null && it.contact_id !== undefined
        ? String(it.contact_id)
        : String(it.contactpersoon_id || ""),
    document_date: String(it.document_date || "").slice(0, 10),
    file_name: it.file_name || "",
    description: it.description || "",
  });
  setSaveError(null);
  setFlash("");
}

async function handleUploadDocument(e) {
  if (isFormLocked || isReadonly) {
    e.target.value = "";
    return;
  }

  const file = e.target.files?.[0];
  if (!file) return;

  setSaveError(null);
  setFlash("");

  try {
    const selectedRow =
      items.find((it) => String(it.id) === String(selectedId || "")) || null;

    const organisationId =
      String(
        form.organisation_id ||
        selectedRow?.organisation_id ||
        selectedRow?.organisatie_id ||
        orgFilterId ||
        ""
      ).trim();

    const contactId =
      String(
        form.contact_id ||
        selectedRow?.contact_id ||
        selectedRow?.contactpersoon_id ||
        contactFilterId ||
        ""
      ).trim();

    const documentType =
      String(
        form.document_type ||
        selectedRow?.document_type ||
        type ||
        "general"
      ).trim() || "general";

    const description = String(form.description || "").trim();
    const documentDate =
      String(form.document_date || selectedRow?.document_date || todayISO()).slice(0, 10);

    const data = new FormData();
    data.append("file", file);
    data.append("organisation_id", organisationId);
    data.append("contact_id", contactId);
    data.append("document_type", documentType);
    data.append("description", description);
    data.append("document_date", documentDate);

    const token = localStorage.getItem("token");

    const res = await fetch(buildUrl("/api/documents/upload"), {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: data,
    });

    if (!res.ok) {
      const msg = await res.text().catch(() => "");
      throw new Error(msg || "Upload mislukt");
    }

    const json = await res.json();
    const savedDoc = json?.document || null;

    if (savedDoc) {
      setSelectedId(savedDoc.id);
      setForm({
        id: savedDoc.id,
        document_type: savedDoc.document_type || documentType,
        organisation_id:
          savedDoc.organisation_id !== null && savedDoc.organisation_id !== undefined
            ? String(savedDoc.organisation_id)
            : organisationId,
        contact_id:
          savedDoc.contact_id !== null && savedDoc.contact_id !== undefined
            ? String(savedDoc.contact_id)
            : contactId,
        document_date: String(savedDoc.document_date || documentDate || "").slice(0, 10),
        file_name:
          savedDoc.file_name
            ? String(savedDoc.file_name)
            : String(file.name || ""),
        description: savedDoc.description || description || "",
      });
    }

    setFlash("Document geüpload ✅");
    setReloadKey((k) => k + 1);

  } catch (err) {
    console.error(err);
    alert(err.message || "Upload mislukt");
  } finally {
    e.target.value = "";
  }
}

function jumpToRightColumn() {
    setMode("form");

    if (window.innerWidth <= 768) {
      focusFirstRightFieldMobile();
    } else {
      setTimeout(() => {
        setFocusIndex(0);
        focusByIndex(0);
      }, 0);
    }
  }

  function focusFirstRightFieldMobile() {
    setTimeout(() => {
      try {
        const panel = rightScrollRef.current;
        if (!panel) return;

        // Eerst het rechter formulierpaneel intern naar boven
        try {
          panel.scrollTop = 0;
        } catch {}

        // Daarna het hele formulierblok in beeld brengen,
        // gelijk aan de werkende methode in Organisaties.js
        panel.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}
    }, 120);
  }

async function openDocument(it) {
  const id = it?.id;
  if (!id) return;

  if (!it?.storage_path) {
    alert("Aan dit document is nog geen bestand gekoppeld.");
    return;
  }

  const token = localStorage.getItem("token");

  try {
    const res = await fetch(buildUrl(`/api/documents/${id}/open`), {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!res.ok) {
      const msg = await res.text().catch(() => "");
      throw new Error(msg || "Document openen mislukt");
    }

    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const contentType = (res.headers.get("content-type") || "").toLowerCase();
    const storagePath = String(it?.storage_path || "");
    const storageFileName = storagePath.split("/").pop() || "";
    const storageExt =
      storageFileName.includes(".")
        ? `.${storageFileName.split(".").pop()}`
        : "";

    const baseName =
      String(it?.file_name || "").trim() ||
      storageFileName.replace(/\.[^.]+$/, "") ||
      "document";

    const finalFileName = baseName.includes(".")
      ? baseName
      : `${baseName}${storageExt || ""}`;

    const isPdf =
      contentType.includes("pdf") ||
      finalFileName.toLowerCase().endsWith(".pdf");

    const isMobileOpen = window.innerWidth <= 768;

    if (isPdf && !isMobileOpen) {
      const newTab = window.open("", "_blank");

      if (!newTab) {
        window.URL.revokeObjectURL(blobUrl);
        alert(`Document ${finalFileName} kon niet worden geopend.\nControleer of popups zijn toegestaan.`);
        return;
      }

      newTab.document.title = finalFileName || "Document";
      newTab.document.body.style.margin = "0";
      newTab.document.body.style.height = "100vh";

      const iframe = newTab.document.createElement("iframe");
      iframe.src = blobUrl;
      iframe.style.border = "0";
      iframe.style.width = "100%";
      iframe.style.height = "100%";

      newTab.document.body.appendChild(iframe);

      setTimeout(() => {
        try {
          window.URL.revokeObjectURL(blobUrl);
        } catch {}
      }, 60000);

      return;
    }

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = finalFileName;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 2000);

    alert(`Document ${finalFileName} gedownload.\nJe vindt het bestand bij de nieuwe downloads.`);
  } catch (err) {
    alert(err?.message || "Document openen mislukt");
  }
}

  // ---------- LOAD ORGS + CONTACTS ----------
  useEffect(() => {
    let cancelled = false;

    async function loadMeta() {
      try {
        const orgData = await tryFetchJson([
          "/api/organisations?q=",
          "/api/organisaties?q=",
          "/api/organisations",
          "/api/organisaties",
        ]);
        if (!cancelled) setOrgs(Array.isArray(orgData) ? orgData : []);
      } catch {
        if (!cancelled) setOrgs([]);
      }

      try {
        const contactData = await tryFetchJson([
          "/api/contacts?q=",
          "/api/contactpersonen?q=",
          "/api/contacts",
          "/api/contactpersonen",
        ]);
        if (!cancelled) setContacts(Array.isArray(contactData) ? contactData : []);
      } catch {
        if (!cancelled) setContacts([]);
      }
    }

    loadMeta();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- LOAD ITEMS ----------
const serverQuery = useMemo(() => {
  const p = new URLSearchParams();
  if (type) p.set("document_type", type);
  if (orgFilterId) {
    p.set("organisation_id", orgFilterId);
    p.set("organisatie_id", orgFilterId);
  }
  if (contactFilterId) {
    p.set("contact_id", contactFilterId);
    p.set("contactpersoon_id", contactFilterId);
  }
  return p.toString();
}, [type, orgFilterId, contactFilterId]);

  useEffect(() => {
    let cancelled = false;

    async function loadItems() {
      setLoading(true);
      setError(null);
      try {
const data = await tryFetchJson([
  serverQuery ? `/api/documents?${serverQuery}` : `/api/documents`,
]);
        if (cancelled) return;
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadItems();
    return () => {
      cancelled = true;
    };
  }, [serverQuery, reloadKey]);

  // ---------- CLIENT FILTER ----------

const titleColOptions = useMemo(() => {
  const vals = Array.from(
    new Set(items.map((it) => String(pickItemTitle(it) || "").trim()).filter(Boolean))
  );
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const dueColOptions = useMemo(() => {
  const vals = Array.from(
    new Set(items.map((it) => String(formatDateNL(pickItemDueISO(it)) || "").trim()).filter(Boolean))
  );
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const titleHasEmpty = useMemo(
  () => items.some((it) => !String(pickItemTitle(it) || "").trim()),
  [items]
);

const dueHasEmpty = useMemo(
  () => items.some((it) => !String(formatDateNL(pickItemDueISO(it)) || "").trim()),
  [items]
);

const filteredItems = useMemo(() => {
  const qq = norm(q);
  const arr = items.slice();

  const byQ = (it) => {
    if (!qq) return true;
    const hay = norm([
      pickItemTitle(it),
      pickItemOrgName(it),
      pickItemContactName(it),
      pickItemPersonnelName(it),
      pickItemPhone(it),
      pickItemMobile(it),
      pickItemEmail(it),
    ].join(" "));
    return hay.includes(qq);
  };

const byType = (it) =>
  !type ? true : String(it.document_type || "").toLowerCase() === String(type).toLowerCase();

const byTitle = (it) => {
  if (!titleFilter) return true;
  const v = String(pickItemTitle(it) || "").trim();
  if (titleFilter === EMPTY_VAL) return !v;
  return v === titleFilter;
};

const byDue = (it) => {
  if (!dueFilter) return true;
  const v = String(formatDateNL(pickItemDueISO(it)) || "").trim();
  if (dueFilter === EMPTY_VAL) return !v;
  return v === dueFilter;
};

const byOrg = (it) =>
  !orgFilterId ? true : String(it.organisation_id || it.organisatie_id || "") === String(orgFilterId);

  const byContact = (it) =>
    !contactFilterId ? true : String(it.contact_id || it.contactpersoon_id || "") === String(contactFilterId);

  const byPersonnel = (it) => {
    if (!personnelFilter) return true;
    const v = String(pickItemPersonnelName(it) || "").trim();
    if (personnelFilter === EMPTY_VAL) return !v;
    return v === personnelFilter;
  };

  const byPhone = (it) => {
    if (!phoneFilter) return true;
    const v = String(pickItemPhone(it) || "").trim();
    if (phoneFilter === EMPTY_VAL) return !v;
    return v === phoneFilter;
  };

  const byMobile = (it) => {
    if (!mobileFilter) return true;
    const v = String(pickItemMobile(it) || "").trim();
    if (mobileFilter === EMPTY_VAL) return !v;
    return v === mobileFilter;
  };

  const byEmail = (it) => {
    if (!emailFilter) return true;
    const v = String(pickItemEmail(it) || "").trim();
    if (emailFilter === EMPTY_VAL) return !v;
    return v === emailFilter;
  };

return arr.filter(
  (it) =>
    byQ(it) &&
    byType(it) &&
    byTitle(it) &&
    byDue(it) &&
    byOrg(it) &&
    byContact(it) &&
    byPersonnel(it) &&
    byPhone(it) &&
    byMobile(it) &&
    byEmail(it)
);
}, [items, q, type, titleFilter, dueFilter, orgFilterId, contactFilterId, personnelFilter, phoneFilter, mobileFilter, emailFilter]);

  // ---------- SORT ----------
const sortedItems = useMemo(() => {
    const copy = filteredItems.slice();
const dueKey = (it) => {
  const iso = pickItemDueISO(it);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : "";
};

const defaultSort = (a, b) => {
  const ad = dueKey(a);
  const bd = dueKey(b);

  const aNo = ad === "";
  const bNo = bd === "";
  if (aNo !== bNo) return aNo ? 1 : -1;

  if (!aNo && !bNo && ad !== bd) {
    return bd.localeCompare(ad);
  }

  return Number(b.id || 0) - Number(a.id || 0);
};

    const dirMul = sort.dir === "desc" ? -1 : 1;

    const cmpText = (a, b) => {
      const aa = norm(a);
      const bb = norm(b);
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

    const cmpISO = (a, b) => {
      const aa = String(a || "");
      const bb = String(b || "");
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

const customSort = (a, b) => {
      if (sort.key === "default") return defaultSort(a, b);
if (sort.key === "type") return dirMul * cmpText(a.document_type || "", b.document_type || "");
if (sort.key === "title") return dirMul * cmpText(pickItemTitle(a), pickItemTitle(b));
if (sort.key === "due") return dirMul * cmpISO(dueKey(a) || "9999-99-99", dueKey(b) || "9999-99-99");
      if (sort.key === "org") return dirMul * cmpText(pickItemOrgName(a), pickItemOrgName(b));
      if (sort.key === "contact") return dirMul * cmpText(pickItemContactName(a), pickItemContactName(b));
      if (sort.key === "personnel") return dirMul * cmpText(pickItemPersonnelName(a), pickItemPersonnelName(b));
      if (sort.key === "phone") return dirMul * cmpText(pickItemPhone(a), pickItemPhone(b));
      if (sort.key === "mobile") return dirMul * cmpText(pickItemMobile(a), pickItemMobile(b));
      if (sort.key === "email") return dirMul * cmpText(pickItemEmail(a), pickItemEmail(b));

      return defaultSort(a, b);
    };

    copy.sort(customSort);
    return copy;
  }, [filteredItems, sort]);

  const gridCount = Math.min(sortedItems.length, 9999);

  useEffect(() => {
    const n = sortedItems.length;
    if (!n) {
      setListIndex(0);
      return;
    }
    if (listIndex < 0 || listIndex >= n) highlightIndex(0, n);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedItems.length]);

  // ---------- ACTIES ----------
  function scrollTopAll() {
    if (midScrollRef.current) midScrollRef.current.scrollTop = 0;
    if (rightScrollRef.current) rightScrollRef.current.scrollTop = 0;
  }

function vernieuwList() {
  setQ("");
  setType("");
  setTitleFilter("");
  setDueFilter("");
  setOrgFilterId("");
  setContactFilterId("");
  setPhoneFilter("");
  setMobileFilter("");
  setEmailFilter("");

  setSelectedId(null);
  setForm(emptyForm);
  setListIndex(0);

  setSaveError(null);
  setFlash("Vernieuwd ✅");
  setSort({ key: "default", dir: "asc" });

  setReloadKey((k) => k + 1);
  setTimeout(() => setFlash(""), 800);

  setMode("search");
  if (window.innerWidth > 768) {
    setTimeout(() => {
      try {
        searchRef.current?.focus?.();
      } catch {}
    }, 0);
  }

  setTimeout(scrollTopAll, 0);

  bodyDictateActiveRef.current = false;
  bodyDictateRunsRef.current = 0;
  setBodyEditor(false);
}

function newItem() {
  setSelectedId(null);
  setForm({
    ...emptyForm,
    document_date: todayISO(),
    document_type: type || "general",
    organisation_id: orgFilterId || "",
    contact_id: contactFilterId || "",
  });
  setSaveError(null);
  setFlash("");
  setMode("form");

  if (window.innerWidth <= 768) {
    focusFirstRightFieldMobile();
  } else {
    setTimeout(() => {
      setFocusIndex(0);
      focusByIndex(0);
      if (rightScrollRef.current) rightScrollRef.current.scrollTop = 0;
    }, 0);
  }

  bodyDictateActiveRef.current = false;
  bodyDictateRunsRef.current = 0;
  setBodyEditor(false);
}

  async function saveItem() {
    if (isReadonly) {
    alert("Alleen-lezen modus. Opslaan niet toegestaan.");
    return;
  }
setSaveError(null);
    setFlash("");

    const documentType = String(form.document_type || "").trim();
    const fileName = String(form.file_name || "").trim();
    const description = String(form.description || "").trim();

    if (!documentType) {
      setSaveError("Kies eerst een documenttype.");
      return;
    }

    if (!fileName && !description) {
      setSaveError("Vul minimaal een bestandsnaam of omschrijving in.");
      return;
    }

const payload = {
  id: form.id,
  document_type: documentType,
  organisation_id: form.organisation_id || null,
  contact_id: form.contact_id || null,
  document_date: form.document_date || null,
  file_name: fileName,
  description: description,
};

    const isUpdate = formHasId();
    const method = isUpdate ? "PUT" : "POST";

const urls = isUpdate
  ? [`/api/documents/${form.id}`]
  : ["/api/documents"];

    try {
      
const saved = await tryFetchJson(urls, {
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(payload),
});

if (!saved || typeof saved !== "object") {
  setReloadKey((k) => k + 1);
  setSelectedId(null);
  setForm(emptyForm);
  setListIndex(0);

  setFlash("Opgeslagen ✅");
  setTimeout(() => setFlash(""), 800);

  setMode("search");
  setTimeout(() => searchRef.current?.focus?.(), 0);
  return;
}

const savedOrg =
  orgs.find(
    (o) =>
      String(o.id) === String(saved.organisation_id ?? form.organisation_id)
  ) || null;

const savedContact =
  contacts.find(
    (c) =>
      String(c.id) === String(saved.contact_id ?? form.contact_id)
  ) || null;

const savedRow = {
  ...saved,

  document_type: saved.document_type ?? form.document_type ?? "",
  file_name: saved.file_name ?? form.file_name ?? "",
  description: saved.description ?? form.description ?? "",
  document_date: (saved.document_date || form.document_date || "").toString().slice(0, 10),

  organisation_id: saved.organisation_id ?? form.organisation_id ?? null,
  contact_id: saved.contact_id ?? form.contact_id ?? null,

  organisation_name:
    saved.organisation_name || pickOrgName(savedOrg),

  contact_name:
    saved.contact_name || pickContactName(savedContact),

  organisation_phone:
    saved.organisation_phone ??
    saved.phone ??
    saved.telefoon ??
    savedOrg?.phone ??
    "",

  organisation_mobile:
    saved.organisation_mobile ??
    saved.mobile ??
    saved.mobiel ??
    savedOrg?.mobile ??
    "",

  organisation_email:
    saved.organisation_email ??
    saved.email ??
    savedOrg?.email ??
    "",
};

setItems((prev) => upsertById(prev, savedRow));

// na opslaan ook filters/zoek leegmaken,
// anders kan het nieuwe document verborgen blijven
setQ("");
setType("");
setTitleFilter("");
setDueFilter("");
setOrgFilterId("");
setContactFilterId("");
setPhoneFilter("");
setMobileFilter("");
setEmailFilter("");

setSelectedId(null);
setForm(emptyForm);
setListIndex(0);
setSort({ key: "default", dir: "asc" });

setFlash("Opgeslagen ✅");
setTimeout(() => setFlash(""), 800);

setMode("search");
setReloadKey((k) => k + 1);
setTimeout(() => searchRef.current?.focus?.(), 0);
return;

    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function deleteItem() {
    if (isReadonly) {
    alert("Alleen-lezen modus. Verwijderen niet toegestaan.");
    return;
  }
    setSaveError(null);
    setFlash("");
    if (!formHasId()) return;

    const ok = window.confirm("Weet je zeker dat je dit item wilt verwijderen?");
    if (!ok) return;

    const id = form.id;
const urls = [`/api/documents/${id}`];

    try {
      await tryFetchJson(urls, { method: "DELETE" });

      setItems((prev) => removeById(prev, id));

      setSelectedId(null);
      setForm(emptyForm);
      setListIndex(0);

      setFlash("Verwijderd ✅");
      setTimeout(() => setFlash(""), 800);

      setMode("search");
      setTimeout(() => searchRef.current?.focus?.(), 0);
      return;
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  // ---------- refs tegen stale state ----------
  const listIndexRef = useRef(0);
  const modeRef = useRef("search");
  const focusIndexRef = useRef(0);
  const lenRef = useRef(0);

  useEffect(() => { listIndexRef.current = listIndex; }, [listIndex]);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { focusIndexRef.current = focusIndex; }, [focusIndex]);
  useEffect(() => { lenRef.current = sortedItems.length; }, [sortedItems.length]);

  // anti-dubbel-commando lock
  const cmdLockRef = useRef({ key: "", ts: 0 });
  function commandLocked(key, ms = 450) {
    const now = Date.now();
    if (cmdLockRef.current.key === key && now - cmdLockRef.current.ts < ms) return true;
    cmdLockRef.current = { key, ts: now };
    return false;
  }

  // ---------- SPEECH ----------
  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 4500, // blijft jouw stabiele PTT
    onCommand: (cmd, rawHeard) => {
      if (!cmd || !cmd.type) return false;

      setSpeechInfo(`🎙 ${rawHeard}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      // als we bezig zijn met door-dicteren in Tekst, dan mag "stop/klaar" dat beëindigen
      if (bodyDictateActiveRef.current && (cmd.type === "clear" || cmd.type === "refresh" || cmd.type === "save" || cmd.type === "delete")) {
        bodyDictateActiveRef.current = false;
        bodyDictateRunsRef.current = 0;
      }

      if (cmd.type === "next") {
        if (commandLocked("next")) return true;
        const m = modeRef.current;
        if (m === "form") { focusNext(); return true; }
        const n = lenRef.current;
        if (!n) return true;
        highlightIndex(Math.min(n - 1, listIndexRef.current + 1), n);
        return true;
      }

      if (cmd.type === "prev") {
        if (commandLocked("prev")) return true;
        const m = modeRef.current;
        if (m === "form") { focusPrev(); return true; }
        const n = lenRef.current;
        if (!n) return true;
        highlightIndex(Math.max(0, listIndexRef.current - 1), n);
        return true;
      }

      if (cmd.type === "new") {
        if (commandLocked("new", 700)) return true;
        newItem();
        return true;
      }

      if (cmd.type === "refresh") {
        if (commandLocked("refresh", 700)) return true;
        vernieuwList();
        return true;
      }

      if (cmd.type === "save") {
        if (commandLocked("save", 700)) return true;
        saveItem();
        return true;
      }

      if (cmd.type === "delete") {
        if (commandLocked("delete", 700)) return true;
        deleteItem();
        return true;
      }

      if (cmd.type === "select") {
        if (commandLocked("select", 500)) return true;
        const row = sortedItems[listIndexRef.current];
        if (row) {
          loadItemFromRow(row);
          setMode("form");
          setTimeout(() => { setFocusIndex(0); focusByIndex(0); }, 0);
        }
        return true;
      }

      if (cmd.type === "list") {
        setMode("list");
        return true;
      }

if (cmd.type === "clear") {
  const m = modeRef.current;
  if (m === "search") { setQ(""); return true; }
  if (m === "form") {
    const key = formOrder[focusIndexRef.current];
    setForm((f) => {
      const nf = { ...f };
      if (key === "title") nf.file_name = "";
      if (key === "body") nf.description = "";
      if (key === "due") nf.document_date = "";
      return nf;
    });
    return true;
  }
  return true;
}

if (cmd.type === "punt") {
  if (modeRef.current === "form") {
    const key = formOrder[focusIndexRef.current];
    if (key === "title") setForm((f) => ({ ...f, file_name: replacePuntToken((f.file_name || "") + " .") }));
    if (key === "body") setForm((f) => ({ ...f, description: replacePuntToken((f.description || "") + " .") }));
  }
  return true;
}

      return false;
    },

    onDictation: (text) => {
      const raw0 = String(text || "").trim();
      if (!raw0) return;

      setSpeechInfo(`🎙 ${raw0}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      const t = norm(raw0);
      const w1 = firstWord(t);

      // EXTRA: stop/klaar tijdens Tekst-dicteren
      if (bodyDictateActiveRef.current) {
        if (t === "stop" || t === "klaar" || t === "klaar met tekst" || t === "einde") {
          bodyDictateActiveRef.current = false;
          bodyDictateRunsRef.current = 0;
          return;
        }
      }

      // snelle dictation-commando’s
      if (w1 === "vernieuw" || w1 === "vernieuwen") {
        if (!commandLocked("refresh", 700)) vernieuwList();
        return;
      }
      if (w1 === "opslaan" || w1 === "bewaren") {
        if (!commandLocked("save", 700)) saveItem();
        return;
      }
      if (w1 === "verwijder" || w1 === "verwijderen" || w1 === "delete") {
        if (!commandLocked("delete", 700)) deleteItem();
        return;
      }

      if (w1 === "selecteer" || t === "item" || t === "rechts") {
        if (!commandLocked("select", 500)) {
          const row = sortedItems[listIndexRef.current];
          if (row) {
            loadItemFromRow(row);
            setMode("form");
            setTimeout(() => { setFocusIndex(0); focusByIndex(0); }, 0);
          }
        }
        return;
      }

      if (t === "lijst" || t === "links") {
        setMode("list");
        return;
      }

if (t === "leeg") {
  if (modeRef.current === "search") setQ("");
  if (modeRef.current === "form") {
    const key = formOrder[focusIndexRef.current];
    setForm((f) => {
      const nf = { ...f };
      if (key === "title") nf.file_name = "";
      if (key === "body") nf.description = "";
      if (key === "due") nf.document_date = "";
      return nf;
    });
  }
  return;
}

      // SEARCH
      if (modeRef.current === "search") {
        if (isEmptyWord(raw0)) { setQ(""); return; }
        setQ(raw0);
        setTimeout(() => highlightIndex(0, sortedItems.length), 0);
        return;
      }

      // FORM
      if (modeRef.current === "form") {
        const key = formOrder[focusIndexRef.current];
        const rawVal = isEmptyWord(raw0) ? "" : raw0;

if (key === "type") {
  setForm((f) => ({ ...f, document_type: rawVal }));
  focusNext();
  return;
}

        if (key === "org") {
          if (!rawVal) { setForm((f) => ({ ...f, organisation_id: "" })); focusNext(); return; }
          const hit =
            orgs.find((o) => norm(pickOrgName(o)).startsWith(norm(rawVal))) ||
            orgs.find((o) => norm(pickOrgName(o)).includes(norm(rawVal)));
          setForm((f) => ({ ...f, organisation_id: hit ? String(hit.id) : "" }));
          focusNext();
          return;
        }

        if (key === "contact") {
          if (!rawVal) { setForm((f) => ({ ...f, contact_id: "" })); focusNext(); return; }
          const hit =
            contacts.find((c) => norm(pickContactName(c)).startsWith(norm(rawVal))) ||
            contacts.find((c) => norm(pickContactName(c)).includes(norm(rawVal)));
          setForm((f) => ({ ...f, contact_id: hit ? String(hit.id) : "" }));
          focusNext();
          return;
        }

if (key === "status") {
  return;
}

if (key === "due") {
  if (!rawVal) { setForm((f) => ({ ...f, document_date: "" })); focusNext(); return; }
  const iso = parseDateFromSpeechToISO(rawVal);
  if (iso) { setForm((f) => ({ ...f, document_date: iso })); focusNext(); }
  return;
}

if (key === "title") {
  const v = capFirst(replacePuntToken(rawVal));
  setForm((f) => ({ ...f, file_name: v }));
  focusNext();
  return;
}
if (key === "body") {
  const v = capFirst(replacePuntToken(rawVal));
  setForm((f) => {
    const prev = String(f.description || "").trim();
    const next = prev ? `${prev}\n${v}` : v;
    return { ...f, description: next };
  });

          // ✅ als we in door-dicteren modus zitten: start automatisch nog een keer
          if (bodyDictateActiveRef.current) {
            bodyDictateRunsRef.current += 1;
            if (bodyDictateRunsRef.current <= 25) {
              setTimeout(() => {
                try {
                  if (bodyDictateActiveRef.current) voice.startOnce();
                } catch {}
              }, 250);
            } else {
              bodyDictateActiveRef.current = false;
              bodyDictateRunsRef.current = 0;
            }
          }
          return;
        }
      }
    },
  });

// ---------- LAYOUT ----------
  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;
  const TABLE_MIN_WIDTH = 1160;
  const MID_BOTTOM_OFFSET = isMobile ? 90 : 135;

const colWidths = {
  type: 48,
  title: 86,
  due: 50,
  org: 70,
  contact: 70,
  personnel: 80,
  phone: 70,
  mobile: 70,
  email: 90,
};

  const searchInputStyle = {
    padding: "7px 10px",
    width: 180,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonPrimary = (bg) => ({
    ...buttonStyle,
    background: bg,
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  });

  const buttonDisabledStyle = { ...buttonStyle, opacity: 0.55, cursor: "not-allowed" };

  const selectStyle = {
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    boxSizing: "border-box",
  };

  const tableStyle = { width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" };

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thBase, cursor: "pointer" };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

const rowBgCursor = "#f3f7ff";
const rowBgSelected = "#e6f0ff";

const personnelColOptions = useMemo(() => {
  const vals = Array.from(new Set(items.map((it) => String(pickItemPersonnelName(it) || "").trim()).filter(Boolean)));
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const phoneColOptions = useMemo(() => {
  const vals = Array.from(new Set(items.map((it) => String(pickItemPhone(it) || "").trim()).filter(Boolean)));
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const mobileColOptions = useMemo(() => {
  const vals = Array.from(new Set(items.map((it) => String(pickItemMobile(it) || "").trim()).filter(Boolean)));
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const emailColOptions = useMemo(() => {
  const vals = Array.from(new Set(items.map((it) => String(pickItemEmail(it) || "").trim()).filter(Boolean)));
  return vals.sort((a, b) => a.localeCompare(b, "nl"));
}, [items]);

const personnelHasEmpty = useMemo(() => items.some((it) => !String(pickItemPersonnelName(it) || "").trim()), [items]);
const phoneHasEmpty = useMemo(() => items.some((it) => !String(pickItemPhone(it) || "").trim()), [items]);
const mobileHasEmpty = useMemo(() => items.some((it) => !String(pickItemMobile(it) || "").trim()), [items]);
const emailHasEmpty = useMemo(() => items.some((it) => !String(pickItemEmail(it) || "").trim()), [items]);

  const commandHelp =
    "Commando’s: volgende | vorige/terug | nieuw | vernieuw | opslaan | verwijderen | item/selecteer/rechts | lijst/links | leeg | punt | status open/klaar | (Tekst: stop/klaar)";

  function sortToggle(key) {
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      return { key, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  const deleteEnabled = formHasId();

const saveEnabled =
    !!String(form.document_type || "").trim() &&
    (
      !!String(form.file_name || "").trim() ||
      !!String(form.description || "").trim()
    );

  // ✅ helper: start speak, maar als focus op body + bodyEditor aan => door-dicteren activeren
  function startSpeakSmart() {
    if (!voice.supported || voice.listening) return;

    const inBody = modeRef.current === "form" && formOrder[focusIndexRef.current] === "body";
    if (inBody && bodyEditor) {
      bodyDictateActiveRef.current = true;
      bodyDictateRunsRef.current = 0;
    } else {
      bodyDictateActiveRef.current = false;
      bodyDictateRunsRef.current = 0;
    }

    voice.startOnce();
  }

  return (
    <div>
<div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : `minmax(0, 1fr) ${RIGHT_WIDTH}px`,
          columnGap: GAP,
          rowGap: isMobile ? 22 : 0,
          alignItems: "start",
          minWidth: 0,
        }}
      >
        {/* LINKS */}
        <div style={{ minWidth: 0 }}>
<h1 style={{ marginTop: 0, marginBottom: 10 }}>
            Documenten <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
          </h1>

{/* Kopregel */}
          <>
            {!isMobile ? (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "nowrap",
                }}
              >
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setMode("search");
                    setBodyEditor(false);
                  }}
                  placeholder="Zoek…"
                  style={searchInputStyle}
                  onFocus={() => {
                    setMode("search");
                    setBodyEditor(false);
                  }}
                />

                <div
                  title={`Aantal regels in het grid: ${gridCount}`}
                  style={{
                    minWidth: 52,
                    height: 32,
                    padding: "0 10px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                  }}
                >
                  {gridCount}
                </div>

                <button
                  onClick={startSpeakSmart}
                  disabled={!voice.supported || voice.listening}
                  style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
                  title={
                    bodyEditor
                      ? "In Tekst: 1x klikken = door-dicteren (stop/klaar om te stoppen)"
                      : "1x klikken = 1 commando dicteren"
                  }
                >
                  {voice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
                </button>

                <button
                  onClick={newItem}
                  disabled={isReadonly}
                  style={isReadonly ? buttonDisabledStyle : buttonStyle}
                >
                  ➕ Nieuw
                </button>

                <button onClick={vernieuwList} style={buttonStyle}>
                  🔄 Vernieuw
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleUploadDocument}
                />
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) 58px",
                    gap: 6,
                    marginBottom: 8,
                    alignItems: "center",
                  }}
                >
                  <input
                    ref={searchRef}
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setMode("search");
                      setBodyEditor(false);
                    }}
                    placeholder="Zoek…"
                    style={{
                      ...searchInputStyle,
                      width: "100%",
                      minWidth: 0,
                    }}
                    onFocus={() => {
                      setMode("search");
                      setBodyEditor(false);
                    }}
                  />

                  <div
                    title={`Aantal regels in het grid: ${gridCount}`}
                    style={{
                      height: 34,
                      minHeight: 34,
                      padding: "0 8px",
                      border: "1px solid #d9d9d9",
                      borderRadius: 4,
                      background: "#f7fafc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#1f4e79",
                      boxSizing: "border-box",
                      whiteSpace: "nowrap",
                      lineHeight: "1",
                    }}
                  >
                    {gridCount}
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 6,
                  }}
                >
                  <button
                    onClick={startSpeakSmart}
                    disabled={!voice.supported || voice.listening}
                    style={{
                      ...(!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle),
                      padding: "10px 8px",
                      fontSize: 13,
                    }}
                    title={
                      bodyEditor
                        ? "In Tekst: 1x klikken = door-dicteren (stop/klaar om te stoppen)"
                        : "1x klikken = 1 commando dicteren"
                    }
                  >
                    {voice.listening ? "🎤..." : "🎤 Spreek"}
                  </button>

                  <button
                    onClick={newItem}
                    disabled={isReadonly}
                    style={{
                      ...(isReadonly
                        ? { ...buttonStyle, opacity: 0.5, cursor: "not-allowed" }
                        : buttonStyle),
                      padding: "10px 8px",
                      fontSize: 13,
                    }}
                  >
                    ➕ Nieuw
                  </button>

                  <button
                    onClick={vernieuwList}
                    style={{
                      ...buttonStyle,
                      padding: "10px 8px",
                      fontSize: 13,
                    }}
                  >
                    🔄 Vernieuw
                  </button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleUploadDocument}
                />
              </>
            )}
          </>

<div style={{ marginTop: 10 }}>
            {loading && <div>Laden…</div>}
            {error && <div style={{ color: "crimson" }}>{error}</div>}
            {flash && <div style={{ marginBottom: 8, color: "green" }}>{flash}</div>}
            {saveError && <div style={{ marginBottom: 8, color: "crimson" }}>{saveError}</div>}
            {voice.error && voice.error !== "no-speech" && (
              <div style={{ color: "crimson" }}>Spraakfout: {voice.error}</div>
            )}

          </div>

          {!loading && !error && (
            <div style={{ marginTop: 8, minWidth: 0 }}>
              <div
                ref={midScrollRef}
                style={{
                  overflowX: "scroll",
                  overflowY: "auto",
                  maxHeight: `calc(100vh - ${MID_BOTTOM_OFFSET}px)`,
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  background: "#fff",
                }}
              >
                <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                  <table style={tableStyle}>
<colgroup>
  <col style={{ width: colWidths.type }} />
  <col style={{ width: colWidths.title }} />
  <col style={{ width: colWidths.due }} />
  <col style={{ width: colWidths.org }} />
  <col style={{ width: colWidths.contact }} />
  <col style={{ width: colWidths.personnel }} />
  <col style={{ width: colWidths.phone }} />
  <col style={{ width: colWidths.mobile }} />
  <col style={{ width: colWidths.email }} />
</colgroup>
                    <thead>
<tr>
  <th style={thClickable} onClick={() => sortToggle("type")}>Type{sortArrow("type")}</th>
  <th style={thClickable} onClick={() => sortToggle("title")}>Bestandsnaam{sortArrow("title")}</th>
  <th style={thClickable} onClick={() => sortToggle("due")}>Datum{sortArrow("due")}</th>
  <th style={thClickable} onClick={() => sortToggle("org")}>Relatie{sortArrow("org")}</th>
  <th style={thClickable} onClick={() => sortToggle("contact")}>Contactpersoon{sortArrow("contact")}</th>
  <th style={thClickable} onClick={() => sortToggle("personnel")}>Personeel{sortArrow("personnel")}</th>
  <th style={thClickable} onClick={() => sortToggle("phone")}>Telefoon{sortArrow("phone")}</th>
  <th style={thClickable} onClick={() => sortToggle("mobile")}>Mobiel{sortArrow("mobile")}</th>
  <th style={thClickable} onClick={() => sortToggle("email")}>E-mail{sortArrow("email")}</th>
</tr>

<tr>
  <th style={filterThStyle}>
    <select value={type} onChange={(e) => setType(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      <option value="general">Algemeen</option>
      <option value="Pers_krt">Pers_krt</option>
      <option value="offer">Offerte</option>
      <option value="invoice">Factuur</option>
      <option value="project">Project</option>
      <option value="contract">Contract</option>
      <option value="other">Overig</option>
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={titleFilter} onChange={(e) => setTitleFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {titleHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {titleColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={dueFilter} onChange={(e) => setDueFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {dueHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {dueColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={orgFilterId || ""} onChange={(e) => setOrgFilterId(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {orgs.map((o) => (
        <option key={o.id} value={String(o.id)}>{pickOrgSearchName(o)}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={contactFilterId || ""} onChange={(e) => setContactFilterId(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {contacts.map((c) => (
        <option key={c.id} value={String(c.id)}>{pickContactName(c)}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={personnelFilter} onChange={(e) => setPersonnelFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {personnelHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {personnelColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={phoneFilter} onChange={(e) => setPhoneFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {phoneHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {phoneColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={mobileFilter} onChange={(e) => setMobileFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {mobileHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {mobileColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>

  <th style={filterThStyle}>
    <select value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} style={selectStyle}>
      <option value="">(alle)</option>
      {emailHasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
      {emailColOptions.map((x) => (
        <option key={x} value={x}>{x}</option>
      ))}
    </select>
  </th>
</tr>

                    </thead>

                    <tbody>
                      {sortedItems.map((it, idx) => {
                        const isCursor = idx === listIndex;
                        const isSelected = selectedId === it.id;

                        let bg = "transparent";
                        if (isSelected) bg = rowBgSelected;
                        else if (isCursor) bg = rowBgCursor;

                        const dueISO = pickItemDueISO(it);
                        const dueNL = dueISO ? formatDateNL(dueISO) : "";

                        return (
<tr
  key={it.id}
  ref={(el) => (rowRefs.current[idx] = el)}
  onClick={() => {
    highlightIndex(idx, sortedItems.length);
    loadItemFromRow(it);
    jumpToRightColumn();
    setBodyEditor(false);
  }}
  onDoubleClick={() => openDocument(it)}
  style={{ cursor: "pointer", backgroundColor: bg }}
>

<td style={tdStyle}>{formatDocumentType(it.document_type) || "-"}</td>
<td style={tdStyle} title={pickItemTitle(it) || ""}>{pickItemTitle(it) || "-"}</td>
<td style={tdStyle}>{formatDateNL(pickItemDueISO(it)) || "-"}</td>
<td style={tdStyle}>
  {pickOrgSearchName(
    orgs.find((o) => String(o.id) === String(it.organisation_id || it.organisatie_id || ""))
  ) || pickItemOrgName(it) || "-"}
</td>
<td style={tdStyle}>{pickItemContactName(it) || "-"}</td>
<td style={tdStyle}>{pickItemPersonnelName(it) || "-"}</td>

<td style={tdStyle} title={pickItemPhone(it) || ""}>
  {pickItemPhone(it) ? (
    <button
      type="button"
      onClick={(e) => handlePhoneGridClick(e, pickItemPhone(it))}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`Bellen: ${pickItemPhone(it)}`}
    >
      {pickItemPhone(it)}
    </button>
  ) : (
    "-"
  )}
</td>

<td style={tdStyle} title={pickItemMobile(it) || ""}>
  {pickItemMobile(it) ? (
    <button
      type="button"
      onClick={(e) => handleMobileGridClick(e, pickItemMobile(it))}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`Bellen / SMS / WhatsApp: ${pickItemMobile(it)}`}
    >
      {pickItemMobile(it)}
    </button>
  ) : (
    "-"
  )}
</td>

<td style={tdStyle} title={pickItemEmail(it) || ""}>
  {pickItemEmail(it) ? (
    <button
      type="button"
      onClick={(e) => handleEmailGridClick(e, pickItemEmail(it))}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        color: "#1f4e79",
        textDecoration: "underline",
        cursor: "pointer",
        font: "inherit",
      }}
      title={`E-mailen: ${pickItemEmail(it)}`}
    >
      {pickItemEmail(it)}
    </button>
  ) : (
    "-"
  )}
</td>

                          </tr>
                        );
                      })}

                      {sortedItems.length === 0 && (
                        <tr>
                          <td colSpan={9} style={{ padding: 12, opacity: 0.7 }}>
                            Geen resultaten (filters te streng?)
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RECHTS */}
<div
          ref={rightScrollRef}
          style={{
            width: isMobile ? "100%" : 320,
            minWidth: isMobile ? 0 : 320,
            maxWidth: isMobile ? "100%" : 320,
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
            borderRadius: 10,
            padding: 10,
            boxSizing: "border-box",
            marginTop: isMobile ? 6 : 0,
            paddingTop: isMobile ? 6 : 10,
          }}
        >
          <div style={{ background: "white", borderRadius: 10, padding: 12, boxSizing: "border-box" }}>
            <h3 style={{ marginTop: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Item</span>
            </h3>

            {/* ✅ Als bodyEditor aan staat: toon alleen grote tekst-editor + knoppen */}

              <div style={{ display: "grid", gap: 8 }}>

<div style={{ marginBottom: 6 }}>
  <button
    ref={uploadButtonRef}
    onClick={() => {
      if (isFormLocked || isReadonly) return;
      fileInputRef.current?.click();
    }}
    disabled={isFormLocked || isReadonly}
    style={
      isFormLocked || isReadonly
        ? {
            ...buttonPrimary("#2f7dd1"),
            opacity: 0.5,
            cursor: "not-allowed",
            width: "100%",
            textAlign: "center",
            fontWeight: 700,
          }
        : {
            ...buttonPrimary("#2f7dd1"),
            width: "100%",
            textAlign: "center",
            fontWeight: 700,
          }
    }
  >
    📎 Upload document
  </button>
</div>

<label style={{ fontSize: 13, color: "#333" }}>Documenttype</label>
<select
  ref={formRefs.type}
  value={form.document_type || ""}
  onChange={(e) => setForm({ ...form, document_type: e.target.value })}
  disabled={isFormLocked || isReadonly}
  style={selectStyle}
  onFocus={() => { setMode("form"); setFocusIndex(0); }}
>
  <option value="">(kies)</option>
  <option value="general">Algemeen</option>
  <option value="offer">Offerte</option>
  <option value="invoice">Factuur</option>
  <option value="project">Project</option>
  <option value="contract">Contract</option>
  <option value="other">Overig</option>
</select>

<label style={{ fontSize: 13, color: "#333" }}>Relatie</label>
<select
  ref={formRefs.org}
  value={form.organisation_id}
  disabled={isFormLocked || isReadonly}
                  onChange={(e) =>
  setForm({
    ...form,
    organisation_id: e.target.value,
    contact_id: "",
  })
}
                  style={selectStyle}
                  onFocus={() => { setMode("form"); setFocusIndex(1); }}
                >
                  <option value="">(geen)</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={String(o.id)}>{pickOrgSearchName(o)}</option>
                  ))}
                </select>

<label
                  style={{
                    fontSize: 13,
                    color: "#333",
                    opacity: selectedRelationIsPrivate ? 0.55 : 1,
                  }}
                >
                  Contactpersoon
                </label>
<select
  ref={formRefs.contact}
  value={selectedRelationIsPrivate ? "" : form.contact_id}
  disabled={isFormLocked || isReadonly || selectedRelationIsPrivate}
                  onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                  style={{
                    ...selectStyle,
                    opacity: selectedRelationIsPrivate ? 0.55 : 1,
                    cursor: selectedRelationIsPrivate ? "not-allowed" : "pointer",
                  }}
                  onFocus={() => {
                    if (selectedRelationIsPrivate) return;
                    setMode("form");
                    setFocusIndex(2);
                  }}
                >
                  <option value="">
                    {selectedRelationIsPrivate
                      ? "(niet van toepassing bij particulier)"
                      : "(geen)"}
                  </option>
{!selectedRelationIsPrivate &&
  contacts
    .filter((c) => {
      // alleen actieve contactpersonen
      if (c.active === false) return false;

      // als er een relatie gekozen is -> alleen die relatie
      if (form.organisation_id && String(c.organisation_id) !== String(form.organisation_id)) {
        return false;
      }

      return true;
    })
    .map((c) => (
      <option key={c.id} value={c.id}>
        {pickContactName(c)}
      </option>
    ))}
                </select>

                <label style={{ fontSize: 13, color: "#333" }}>Documentdatum</label>
<input
  ref={formRefs.due}
  type="date"
  disabled={isFormLocked || isReadonly}
  value={String(form.document_date || "").slice(0, 10)}
  onChange={(e) =>
    setForm((prev) => ({
      ...prev,
      document_date: String(e.target.value || "").slice(0, 10),
    }))
  }
  style={{
    padding: "7px 10px",
    width: "100%",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  }}
  onFocus={() => {
    setMode("form");
    setFocusIndex(3);
  }}
/>
                <label style={{ fontSize: 13, color: "#333" }}>Bestandsnaam</label>

<input
  ref={formRefs.title}
  value={form.file_name}
  disabled={isFormLocked || isReadonly}
                  onChange={(e) => setForm({ ...form, file_name: e.target.value })}
                  style={{
                    padding: "7px 10px",
                    width: "100%",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "white",
                    boxSizing: "border-box",
                  }}
                  onFocus={() => { setMode("form"); setFocusIndex(4); }}
                />

<label style={{ fontSize: 13, color: "#333" }}>Omschrijving</label>
<textarea
  ref={formRefs.body}
  value={form.description}
  disabled={isFormLocked || isReadonly}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={8}
                  style={{
                    padding: "7px 10px",
                    width: "100%",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "white",
                    resize: "vertical",
                    boxSizing: "border-box",
                  }}
                  onFocus={() => {
                    setMode("form");
                    setFocusIndex(5);
                  }}
                />

<div style={{ display: "grid", gap: 8, marginTop: 6 }}>
  {formHasId() && (
    <button
      onClick={() => {
        const currentItem = items.find((it) => String(it.id) === String(form.id));
        if (currentItem) openDocument(currentItem);
      }}
      style={{
        ...buttonPrimary("#6c757d"),
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      ⬇️ Download document
    </button>
  )}

  <div style={{ display: "flex", gap: 10 }}>
    <button
      onClick={saveItem}
      disabled={isFormLocked || !saveEnabled || isReadonly}
      style={
        isFormLocked || !saveEnabled || isReadonly
          ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
          : buttonPrimary("#2f7dd1")
      }
    >
      💾 Opslaan
    </button>

    <button
      onClick={deleteItem}
      disabled={isFormLocked || !deleteEnabled || isReadonly}
      style={
        isFormLocked || !deleteEnabled || isReadonly
          ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
          : buttonPrimary("#d64545")
      }
    >
      🗑 Verwijderen
    </button>
  </div>
</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}